import React, {FC, useState} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {ResolveDispatchProps} from '../../helpers/reduxHelpers';
import {makeMapDispatchToProps, makeMapStateToProps, ResolveStateProps} from '../../helpers/reselectHelpers';
import {SettingsAnswersUrl, SettingsResourcePagesEnum} from '../../models/url/settingsUrlModel';
import {AppState} from '../../store/appState';
import {getAnswerFolders} from '../../store/data/answerFolders/answerFolderSelectors';
import {
  AnswerFolderTemplate,
  createAnswerFolderAsyncAction,
  saveAnswerFolderAsyncAction,
} from '../../store/data/answerFolders/saveFolderActions';
import {getAnswers} from '../../store/data/answers/answersSelectors';
import {
  AnswerTemplate,
  createAnswerFromTemplateAsyncAction,
  saveAnswerAsyncAction,
} from '../../store/data/answers/saveAnswerActions';
import {makeGetInboxesByNamespace} from '../../store/data/inboxes/inboxesSelectors';
import {DeleteAnswerDialogContainer} from './deleteAnswerDialogContainer';
import {SettingsAnswersNavigationProps} from './settingsAnswers';
import {SettingsSingleAnswer} from './settingsSingleAnswer';
import {SettingsSingleAnswerFolder} from './settingsSingleAnswerFolder';

/*
 * Props.
 */

interface SettingSingleAnswerContainerProps
  extends Pick<SettingsAnswersNavigationProps, 'navigateToFolder' | 'openRoot'> {
  answersUrl: SettingsAnswersUrl;
  isEditing?: boolean;
  prefilledFolderId?: number;
}

/*
 * Selectors.
 */

function getAnswersUrl(_state: AppState, props: SettingSingleAnswerContainerProps) {
  return props.answersUrl;
}

function getNamespace(_state: AppState, props: SettingSingleAnswerContainerProps) {
  return props.answersUrl.namespace;
}

function makeGetAnswer() {
  return createSelector([getAnswersUrl, getAnswers], (answersUrl, answers) => {
    const {answerId, resourcePage} = answersUrl;
    if (resourcePage === SettingsResourcePagesEnum.NEW) {
      return undefined;
    }

    return answerId ? answers[answerId] : undefined;
  });
}

function makeGetFolder() {
  return createSelector([getAnswersUrl, getAnswerFolders], (answersUrl, folders) => {
    const {folderId, resourcePage} = answersUrl;
    if (resourcePage === SettingsResourcePagesEnum.NEW) {
      return undefined;
    }

    return folderId ? folders[folderId] : undefined;
  });
}

function makeGetParentFolder(
  getAnswer: ReturnType<typeof makeGetAnswer>,
  getFolder: ReturnType<typeof makeGetFolder>,
) {
  return createSelector([getAnswer, getFolder, getAnswerFolders], (answer, folder, folders) => {
    if ((!answer && !folder) || (answer && folder)) {
      return undefined;
    }

    if (answer?.folderId) {
      return folders[answer.folderId];
    }

    if (folder?.parentFolderId) {
      return folders[folder.parentFolderId];
    }

    return undefined;
  });
}

/*
 * Component.
 */

interface SettingsSingleAnswerContainerComponentProps
  extends SettingSingleAnswerContainerProps,
    ResolveStateProps<typeof mapStateToProps>,
    ResolveDispatchProps<typeof mapDispatchToProps> {}

const SettingsSingleAnswerContainerComponent: FC<SettingsSingleAnswerContainerComponentProps> = (props) => {
  const {answersUrl, navigateToFolder, openRoot, answer, isEditing, inboxes, folder} = props;
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);

  const isRenderingFolder = folder || (!folder && !answer && answersUrl.inFolder);

  const onSaveAnswerClick = async (template: AnswerTemplate) => {
    if (isEditing && answer) {
      return props.saveAnswerAsync(answer.id, template);
    }

    return props.createAnswerFromTemplateAsync(template);
  };

  const onSaveAnswerFolderClick = async (template: AnswerFolderTemplate) => {
    if (isEditing && folder) {
      return props.saveAnswerFolder(folder.id, template);
    }

    return props.createAnswerFolder(template);
  };

  const maybeRenderDeleteConfirmDialog = () => {
    if (!isDeleteConfirmationOpen) {
      return null;
    }

    const itemToActOn = isRenderingFolder ? folder : answer;
    const folderId = isRenderingFolder && folder ? folder.parentFolderId : answer?.folderId;

    if (!itemToActOn) {
      return null;
    }

    return (
      <DeleteAnswerDialogContainer
        itemsToActOn={[itemToActOn]}
        answersUrl={answersUrl}
        onRequestClose={() => setIsDeleteConfirmationOpen(false)}
        onDelete={() => {
          if (folderId) {
            navigateToFolder(folderId);
          } else {
            openRoot();
          }
        }}
      />
    );
  };

  if (isRenderingFolder) {
    return (
      <>
        {maybeRenderDeleteConfirmDialog()}
        <SettingsSingleAnswerFolder
          prefilledFolderId={props.prefilledFolderId}
          answersUrl={answersUrl}
          navigateToFolder={navigateToFolder}
          openRoot={openRoot}
          folder={folder}
          isEditing={isEditing}
          onSaveClick={onSaveAnswerFolderClick}
          onDeleteClick={() => {
            setIsDeleteConfirmationOpen(true);
          }}
        />
      </>
    );
  }

  return (
    <>
      {maybeRenderDeleteConfirmDialog()}
      <SettingsSingleAnswer
        prefilledFolderId={props.prefilledFolderId}
        answersUrl={answersUrl}
        navigateToFolder={navigateToFolder}
        openRoot={openRoot}
        answer={answer}
        isEditing={isEditing}
        onSaveClick={onSaveAnswerClick}
        onDeleteClick={() => setIsDeleteConfirmationOpen(true)}
        inboxes={inboxes}
      />
    </>
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<SettingSingleAnswerContainerProps>()(() => {
  const getAnswer = makeGetAnswer();
  const getFolder = makeGetFolder();

  return {
    answer: getAnswer,
    folder: getFolder,
    parentFolder: makeGetParentFolder(getAnswer, getFolder),
    inboxes: makeGetInboxesByNamespace(getNamespace),
  };
});

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  createAnswerFromTemplateAsync: createAnswerFromTemplateAsyncAction,
  saveAnswerAsync: saveAnswerAsyncAction,
  createAnswerFolder: createAnswerFolderAsyncAction,
  saveAnswerFolder: saveAnswerFolderAsyncAction,
}));

export const SettingsSingleAnswerContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(SettingsSingleAnswerContainerComponent);
